
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthHandlers } from "@/hooks/useAuthHandlers";

interface PasswordResetFormProps {
  onError: (error: string) => void;
  onBackToLogin: () => void;
}

const PasswordResetForm = ({ onError, onBackToLogin }: PasswordResetFormProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { loading, setLoading, handlePasswordReset } = useAuthHandlers();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await handlePasswordReset(newPassword, confirmPassword);
      // Clear recovery state from session storage
      sessionStorage.removeItem('isPasswordRecovery');
      // After successful password reset, user is automatically logged in
      // so we redirect to dashboard
      navigate("/dashboard", { replace: true });
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    // Clear recovery state when going back
    sessionStorage.removeItem('isPasswordRecovery');
    onBackToLogin();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="newPassword" className="text-sm">New Password</Label>
        <Input
          id="newPassword"
          type="password"
          placeholder="Enter new password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="h-11 sm:h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-base"
          required
        />
      </div>

      <div>
        <Label htmlFor="confirmPassword" className="text-sm">Confirm New Password</Label>
        <Input
          id="confirmPassword"
          type="password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="h-11 sm:h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-base"
          required
        />
      </div>

      <Button
        type="submit"
        className="w-full h-11 sm:h-12 bg-slate-800 hover:bg-slate-900 text-white text-base"
        disabled={loading}
      >
        {loading ? "Updating..." : "Update Password"}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={handleBackClick}
          className="text-sm text-purple-600 hover:underline"
        >
          Back to sign in
        </button>
      </div>
    </form>
  );
};

export default PasswordResetForm;
